import { create } from 'zustand'
import { supabase } from './lib/supabase'
import { incomeOccursInMonth } from './lib/payday'

// ============================================================================
// Global data store - single source of truth for all shared entities.
// Pages read slices via the compatibility hooks in hooks/useData.js, which now
// delegate here. One realtime subscription set, so a change on any page
// propagates to every page instantly without navigation.
// ============================================================================

const sum = (arr, key) => arr.reduce((s, x) => s + (x[key] || 0), 0)

export const useStore = create((set, get) => ({
  userId: null,
  ready: false,

  accounts: [],
  debts: [],
  goals: [],
  bills: [],
  investments: [],
  investmentSnapshots: [],
  incomeEntries: [],
  netWorthHistory: [],
  quickSpends: [],
  quickIncome: [],

  loading: {
    accounts: true, debts: true, goals: true, bills: true,
    investments: true, income: true, quickSpends: true, quickIncome: true,
  },

  // ── Fetchers ──────────────────────────────────────────────
  async fetchAccounts() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('accounts').select('*').eq('user_id', uid).order('created_at')
    set(s => ({ accounts: data || [], loading: { ...s.loading, accounts: false } }))
  },
  async fetchDebts() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('debts').select('*').eq('user_id', uid).order('priority')
    set(s => ({ debts: data || [], loading: { ...s.loading, debts: false } }))
  },
  async fetchGoals() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('goals').select('*').eq('user_id', uid).order('created_at')
    set(s => ({ goals: data || [], loading: { ...s.loading, goals: false } }))
  },
  async fetchBills() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('bills').select('*').eq('user_id', uid).eq('is_active', true).order('due_day')
    set(s => ({ bills: data || [], loading: { ...s.loading, bills: false } }))
  },
  async fetchInvestments() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('investments').select('*').eq('user_id', uid).order('created_at')
    set(s => ({ investments: data || [], loading: { ...s.loading, investments: false } }))
  },
  async fetchInvestmentSnapshots() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('investment_snapshots').select('*').eq('user_id', uid).order('month').limit(24)
    set({ investmentSnapshots: data || [] })
  },
  async fetchIncomeEntries() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('income_entries').select('*').eq('user_id', uid).order('received_on', { ascending: false }).limit(400)
    set(s => ({ incomeEntries: data || [], loading: { ...s.loading, income: false } }))
  },
  async fetchNetWorthHistory() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('net_worth_snapshots').select('*').eq('user_id', uid).order('month').limit(400)
    set({ netWorthHistory: data || [] })
  },
  async fetchQuickSpends() {
    const uid = get().userId; if (!uid) return
    // Only the current/recent cycles matter for the UI; cap at 200 to keep
    // payloads small. Cycle-filtering happens in the component, not here.
    const { data } = await supabase.from('quick_spends').select('*').eq('user_id', uid).order('created_at', { ascending: false }).limit(200)
    set(s => ({ quickSpends: data || [], loading: { ...s.loading, quickSpends: false } }))
  },

  async fetchQuickIncome() {
    const uid = get().userId; if (!uid) return
    const { data } = await supabase.from('quick_income').select('*').eq('user_id', uid).order('created_at', { ascending: false }).limit(200)
    set(s => ({ quickIncome: data || [], loading: { ...s.loading, quickIncome: false } }))
  },

  // ── Mutations ─────────────────────────────────────────────
  async upsertAccount(account) {
    const uid = get().userId
    const payload = { ...account, user_id: uid }
    const { data, error } = account.id
      ? await supabase.from('accounts').update(payload).eq('id', account.id).select().single()
      : await supabase.from('accounts').insert(payload).select().single()
    if (!error) {
      await get().fetchAccounts()
      // Track when balances were last updated for the staleness indicator
      localStorage.setItem('tic_balances_updated_at_' + (get().userId || ''), new Date().toISOString())
    }
    return { data, error }
  },
  async deleteAccount(id) {
    await supabase.from('accounts').delete().eq('id', id)
    await get().fetchAccounts()
  },
  async upsertDebt(debt) {
    const uid = get().userId
    const payload = { ...debt, user_id: uid }
    const { data, error } = debt.id
      ? await supabase.from('debts').update(payload).eq('id', debt.id).select().single()
      : await supabase.from('debts').insert(payload).select().single()
    if (!error) await get().fetchDebts()
    return { data, error }
  },
  async deleteDebt(id) {
    await supabase.from('debts').delete().eq('id', id)
    await get().fetchDebts()
  },
  async upsertGoal(goal) {
    const uid = get().userId
    const payload = { ...goal, user_id: uid }
    const { data, error } = goal.id
      ? await supabase.from('goals').update(payload).eq('id', goal.id).select().single()
      : await supabase.from('goals').insert(payload).select().single()
    if (!error) await get().fetchGoals()
    return { data, error }
  },
  async deleteGoal(id) {
    await supabase.from('goals').delete().eq('id', id)
    await get().fetchGoals()
  },
  async upsertBill(bill) {
    const uid = get().userId
    const payload = { ...bill, user_id: uid }
    const { data, error } = bill.id
      ? await supabase.from('bills').update(payload).eq('id', bill.id).select().single()
      : await supabase.from('bills').insert(payload).select().single()
    if (error) console.error('Bills save error:', error)
    if (!error) await get().fetchBills()
    return { data, error }
  },
  async deleteBill(id) {
    await supabase.from('bills').update({ is_active: false }).eq('id', id)
    await get().fetchBills()
  },
  async upsertInvestment(inv) {
    const uid = get().userId
    const payload = { ...inv, user_id: uid }
    const { data, error } = inv.id
      ? await supabase.from('investments').update(payload).eq('id', inv.id).select().single()
      : await supabase.from('investments').insert(payload).select().single()
    if (!error) await get().fetchInvestments()
    return { data, error }
  },
  async deleteInvestment(id) {
    await supabase.from('investments').delete().eq('id', id)
    await get().fetchInvestments()
  },
  async saveInvestmentSnapshot(month, totalValue) {
    const uid = get().userId
    await supabase.from('investment_snapshots').upsert({ user_id: uid, month, total_value: totalValue }, { onConflict: 'user_id,month' })
    await get().fetchInvestmentSnapshots()
  },
  async upsertIncomeEntry(entry) {
    const uid = get().userId
    const payload = { ...entry, user_id: uid }
    const { data, error } = entry.id
      ? await supabase.from('income_entries').update(payload).eq('id', entry.id).select().single()
      : await supabase.from('income_entries').insert(payload).select().single()
    if (error) console.error('Income save error:', error)
    if (!error) await get().fetchIncomeEntries()
    return { data, error }
  },
  async deleteIncomeEntry(id) {
    await supabase.from('income_entries').delete().eq('id', id)
    await get().fetchIncomeEntries()
  },
  async saveSnapshot(totalAssets, totalDebts) {
    const uid = get().userId
    const now = new Date()
    const dailyKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`
    const net_worth = totalAssets - totalDebts
    await supabase.from('net_worth_snapshots')
      .upsert({ user_id: uid, month: dailyKey, total_assets: totalAssets, total_debts: totalDebts, net_worth }, { onConflict: 'user_id,month' })
    await get().fetchNetWorthHistory()
  },
  async saveReport(month, data) {
    const uid = get().userId
    await supabase.from('monthly_reports').upsert({ user_id: uid, month, ...data }, { onConflict: 'user_id,month' })
  },

  // ── Quick-spends ──────────────────────────────────────────
  // Isolated from budgets / reports / net worth. The whole point is a one-tap
  // decrement of the safe-to-spend pot between balance updates.
  async addQuickSpend({ amount, note, payCycleStart, budget_category, account_id }) {
    const uid = get().userId
    if (!uid || !payCycleStart) return { data: null, error: new Error('missing user or cycle') }
    const base = {
      user_id: uid,
      amount: Number(amount) || 0,
      note: note || null,
      pay_cycle_start: payCycleStart,
      ...(account_id ? { account_id } : {}),
    }
    // Try with budget_category first; fall back without it if the column doesn't exist yet
    const withCat = budget_category ? { ...base, budget_category } : base
    let { data, error } = await supabase.from('quick_spends').insert(withCat).select().single()
    if (error && budget_category && error.message?.includes('budget_category')) {
      // Column missing - retry without it
      console.warn('budget_category column missing, saving without it. Run: ALTER TABLE quick_spends ADD COLUMN IF NOT EXISTS budget_category text')
      ;({ data, error } = await supabase.from('quick_spends').insert(base).select().single())
    }
    if (error) console.error('Quick-spend save error:', error)
    if (!error) await get().fetchQuickSpends()
    return { data, error }
  },
  async addQuickIncome({ amount, note, category, ownerLabel, payCycleStart }) {
    const uid = get().userId
    if (!uid || !payCycleStart) return { data: null, error: new Error('missing user or cycle') }
    const base = { user_id: uid, amount: Number(amount) || 0, note: note || null, pay_cycle_start: payCycleStart }
    const payload = { ...base, ...(category ? { category } : {}), ...(ownerLabel ? { owner_label: ownerLabel } : {}) }
    const { data, error } = await supabase.from('quick_income').insert(payload).select().single()
    if (error) console.error('Quick-income save error:', error)
    if (!error) await get().fetchQuickIncome()
    return { data, error }
  },

  async updateQuickIncome(id, patch) {
    const { error } = await supabase.from('quick_income').update(patch).eq('id', id)
    if (!error) await get().fetchQuickIncome()
    return { error }
  },
  async deleteQuickIncome(id) {
    await supabase.from('quick_income').delete().eq('id', id)
    await get().fetchQuickIncome()
  },

  async updateQuickSpend(id, patch) {
    const { error } = await supabase.from('quick_spends').update(patch).eq('id', id)
    if (!error) await get().fetchQuickSpends()
    return { error }
  },
  async deleteQuickSpend(id) {
    await supabase.from('quick_spends').delete().eq('id', id)
    await get().fetchQuickSpends()
  },
  async clearQuickSpendsForCycle(payCycleStart) {
    const uid = get().userId
    if (!uid || !payCycleStart) return
    await supabase.from('quick_spends').delete().eq('user_id', uid).eq('pay_cycle_start', payCycleStart)
    await get().fetchQuickSpends()
  },

  // ── Lifecycle ─────────────────────────────────────────────
  _channel: null,
  async init(userId) {
    if (!userId || get().userId === userId) return
    set({ userId })
    // Initial fetch of everything
    await Promise.all([
      get().fetchAccounts(), get().fetchDebts(), get().fetchGoals(),
      get().fetchBills(), get().fetchInvestments(), get().fetchInvestmentSnapshots(),
      get().fetchIncomeEntries(), get().fetchNetWorthHistory(),
      get().fetchQuickSpends(),
      get().fetchQuickIncome(),
    ])
    set({ ready: true })

    // One realtime channel covering every table that can change.
    const ch = supabase.channel(`store-${userId}`)
    const tables = {
      accounts: () => get().fetchAccounts(),
      debts: () => get().fetchDebts(),
      goals: () => get().fetchGoals(),
      bills: () => get().fetchBills(),
      investments: () => get().fetchInvestments(),
      income_entries: () => get().fetchIncomeEntries(),
      quick_spends: () => get().fetchQuickSpends(),
        quick_income: () => get().fetchQuickIncome(),
    }
    Object.entries(tables).forEach(([table, refetch]) => {
      ch.on('postgres_changes', { event: '*', schema: 'public', table, filter: `user_id=eq.${userId}` }, refetch)
    })
    ch.subscribe()
    set({ _channel: ch })

    // Refetch on tab focus
    const onVisible = () => { if (document.visibilityState === 'visible') {
      get().fetchAccounts(); get().fetchDebts(); get().fetchGoals()
      get().fetchBills(); get().fetchInvestments(); get().fetchIncomeEntries()
      get().fetchQuickSpends()
    get().fetchQuickIncome()
    } }
    document.addEventListener('visibilitychange', onVisible)
    set({ _onVisible: onVisible })
  },
  teardown() {
    const ch = get()._channel
    if (ch) supabase.removeChannel(ch)
    const ov = get()._onVisible
    if (ov) document.removeEventListener('visibilitychange', ov)
    set({ userId: null, ready: false, _channel: null, _onVisible: null })
  },
}))

// ── Derived selectors (computed from store state) ───────────
export const selectTotalAssets = s => sum(s.accounts, 'balance')
export const selectTotalDebt = s => sum(s.debts, 'current_balance')
export const selectTotalInvestments = s => sum(s.investments, 'current_value')
export const selectMonthlyContributions = s => sum(s.investments, 'monthly_contribution')

// Income helpers - work on dated entries
export function monthlyIncomeForMonth(entries, monthKey) {
  // monthKey: 'YYYY-MM'. Sum scheduled recurring sources + one-offs received that month.
  const [year, month] = monthKey.split('-').map(Number)
  const monthIndex = month - 1  // 0-based
  const monthStart = new Date(year, monthIndex, 1)
  const monthEnd = new Date(year, monthIndex + 1, 0)

  return entries.reduce((total, e) => {
    // Check effective date range
    if (e.started_at && new Date(e.started_at) > monthEnd) return total
    if (e.ended_at && new Date(e.ended_at) < monthStart) return total

    if (e.recurring) {
      if (incomeOccursInMonth(e, year, monthIndex)) return total + (e.amount || 0)
      return total
    }
    if ((e.received_on || '').slice(0, 7) === monthKey) return total + (e.amount || 0)
    return total
  }, 0)
}
